import {
  computeStatusDefault,
  computeStatusExchangeInline,
  computeStatusExchangeMail,
  computeStatusSpOdToGraph,
  computeStatusWithCode,
} from './utils';

export interface NotificationItem {
  type: string;
  status: string;
  hidden: boolean;
  time: string | null;
}

export interface NotificationDict {
  [key: string]: NotificationItem;
}
export interface AllNotificationDict {
  [key: string]: NotificationDict;
}

type ComputeStatusFunc = (state: string, statuses: any[]) => string;
type ComputeHiddenFunc = (args: {
  self: NotificationItem;
  dict: NotificationDict;
  empty: boolean;
  config: { [key: string]: any };
  policyTypes: number[];
  pocProvisionServices: boolean[];
}) => boolean | null;

let globalRMSFlag = false; // need to remove

const CONFIG: {
  [key: string]: {
    defaultStatus: string;
    computeStatus: ComputeStatusFunc;
    defaultHidden: boolean;
    computeHidden: ComputeHiddenFunc | null;
  };
} = {
  NO_SERVICE: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      if (policyTypes.length === 0 && config['STORAGE_ENABLED']) {
        return false;
      }
      return null;
    },
  },
  PROVISION_FOR_EXCHANGE_ONLINE: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    computeHidden: ({ dict, policyTypes }) => {
      if (!policyTypes.includes(1)) {
        return true;
      }
      return null;
    },
  },
  PROVISION_FOR_SHAREPOINT_ONLINE: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ policyTypes }) =>
      !policyTypes.includes(2) ? true : null,
  },
  PROVISION_FOR_ONEDRIVE_FOR_BUSINESS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ policyTypes }) =>
      !policyTypes.includes(3) ? true : null,
  },
  BOX_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(4)) {
        return true;
      } else {
        return !(
          config['STORAGE_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.BOX.ENABLED']
        );
      }
    },
  },
  DROPBOX_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(5)) {
        return true;
      } else {
        return !(
          config['STORAGE_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.DROPBOX.ENABLED']
        );
      }
    },
  },
  GOOGLEDRIVE_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(6)) {
        return true;
      } else {
        return !(
          config['STORAGE_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.GOOGLEDRIVE.ENABLED']
        );
      }
    },
  },
  SALESFORCE_SANDBOX_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(9)) {
        return true;
      } else {
        return !(
          config['STORAGE_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.SALESFORCE_SANDBOX.ENABLED']
        );
      }
    },
  },
  SALESFORCE_PRODUCTION_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(10)) {
        return true;
      } else {
        return !(
          config['STORAGE_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.SALESFORCE_PRODUCTION.ENABLED']
        );
      }
    },
  },
  PROVISION_FOR_RMS: {
    defaultStatus: 'OPTIONAL',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ dict, empty }) => {
      if (empty) {
        return null;
      }
      globalRMSFlag =
        globalRMSFlag ||
        (dict['PROVISION_FOR_SHAREPOINT_ONLINE']?.status || '').includes(
          'SUCCESS'
        ) ||
        (dict['PROVISION_FOR_ONEDRIVE_FOR_BUSINESS']?.status || '').includes(
          'SUCCESS'
        ) ||
        (dict['PROVISION_FOR_MICROSOFT_TEAMS']?.status || '').includes(
          'SUCCESS'
        ) ||
        (dict['PROVISION_FOR_RMS']?.status || '').includes('SUCCESS');
      return globalRMSFlag ? false : null;
    },
  },
  CREATE_LOCAL_LOGIN_ACCOUNT: {
    defaultStatus: 'OPTIONAL',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: null,
  },
  CREATE_ATP_POLICIES: {
    defaultStatus: 'OPTIONAL',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ config }) => {
      return !config[
        'SERVER.POLICYHOLDER_TYPES.Advanced Threat Protection.ENABLED'
      ]
        ? true
        : null;
    },
  },
  CREATE_DP_POLICIES: {
    defaultStatus: 'OPTIONAL',
    computeStatus: computeStatusDefault,
    defaultHidden: false,
    computeHidden: ({ config }) => {
      return !config['SERVER.POLICYHOLDER_TYPES.Data Protection.ENABLED']
        ? true
        : null;
    },
  },
  DELEGATOR_RMS_ACCOUNT_NOTIFICATION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  DELEGATOR_EXCHANGE_ACCOUNT_NOTIFICATION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  DELEGATOR_SHAREPOINT_ACCOUNT_NOTIFICATION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  BOX_ACCOUNT_NOTIFICATION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  DROPBOX_ACCOUNT_NOTIFICATION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  GOOGLEDRIVE_ACCOUNT_NOTIFICATION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_EX_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ self, empty }) =>
      !empty && self.status === 'REQUIRED' ? true : null,
  },
  PROVISION_OFFICE365_ANOMALY: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null, // !!!!!!!!!!
  },
  GMAIL_PROVISION_STATUS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  PROVISION_FOR_MICROSOFT_TEAMS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(41)) {
        return true;
      } else {
        return !(
          config['TEAMS_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.TEAMS.ENABLED']
        );
      }
    },
  },
  GET_NEW_LICENSE_EXPIRING: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  GET_NEW_LICENSE_EXPIRED: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  POC_PROVISION_UPGRADE: {
    defaultStatus: 'OPTIONAL',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: ({ empty, pocProvisionServices: poc }) => {
      return !empty && (poc[1] || poc[2] || poc[3]) ? false : null;
    },
  },
  GRAPH_TM_CHAT_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(42)) {
        return true;
      } else {
        return !(
          config['TEAMS_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.TEAMS.ENABLED']
        );
      }
    },
  },
  MIGRATION_SP_TO_GRAPH_CLEAN_STEPS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusSpOdToGraph,
    defaultHidden: true,
    // computeHidden: (({ self, empty }) => !empty && self.status === 'REQUIRED' ? true : null),
    computeHidden: null,
  },
  MIGRATION_OD_TO_GRAPH_CLEAN_STEPS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusSpOdToGraph,
    defaultHidden: true,
    // computeHidden: (({ self, empty }) => !empty && self.status === 'REQUIRED' ? true : null),
    computeHidden: null,
  },
  GRAPH_MIP_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(46)) {
        return true;
      } else {
        return !(
          config['TEAMS_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.TEAMS.ENABLED']
        );
      }
    },
  },
  PROVISION_FOR_EXCHANGE_MAIL_FLOW_STEPS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusExchangeMail.bind(null, true),
    defaultHidden: true,
    computeHidden: ({ policyTypes }) =>
      !policyTypes.includes(81) ? true : null,
  },
  PROVISION_FOR_EXCHANGE_INLINE_STEPS: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusExchangeInline,
    defaultHidden: true,
    computeHidden: ({ policyTypes, config }) => {
      if (!policyTypes.includes(82)) {
        return true;
      } else {
        return !(
          config['EXCHANGE_INLINE_ENABLED'] &&
          config['SERVER.SERVICE_TYPES.EXCHANGE_INLINE.ENABLED']
        );
      }
    },
  },
  PROVISION_FOR_CAS_API_MICROSOFT_IDENTITY_PROTECTION_API: {
    defaultStatus: 'REQUIRED',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ policyTypes }) =>
      !policyTypes.includes(35) ? true : null,
  },
};

export function generateNotifications(data: {
  statuses: { [key: string]: any[] };
  policyTypes: { [key: string]: number[] };
}) {
  const allNotifications: AllNotificationDict = {};

  for (const [guid, statuses] of Object.entries(data.statuses)) {
    if (statuses.length === 0) {
      continue;
    }
    const dict = generateDefault();
    allNotifications[guid] = dict;

    let empty = true;
    for (const status of statuses) {
      const cfg = CONFIG[status.type];
      if (!cfg) {
        continue;
      }
      empty = false;
      const item = dict[status.type];
      item.status = cfg.computeStatus(status.value, statuses);
      item.time = status.updatedAt || null;
      item.hidden = false;
    }

    const args = {
      self: null as any,
      dict,
      empty,
      config: {
        STORAGE_ENABLED: true,
        'SERVER.SERVICE_TYPES.BOX.ENABLED': true,
        'SERVER.SERVICE_TYPES.DROPBOX.ENABLED': true,
        'SERVER.SERVICE_TYPES.GOOGLEDRIVE.ENABLED': true,
        'SERVER.SERVICE_TYPES.SALESFORCE_SANDBOX.ENABLED': true,
        'SERVER.SERVICE_TYPES.SALESFORCE_PRODUCTION.ENABLED': true,
        'SERVER.POLICYHOLDER_TYPES.Advanced Threat Protection.ENABLED': true,
        'SERVER.POLICYHOLDER_TYPES.Data Protection.ENABLED': true,
        TEAMS_ENABLED: true,
        'SERVER.SERVICE_TYPES.TEAMS.ENABLED': true,
        'SERVER.SERVICE_TYPES.EXCHANGE_INLINE.ENABLED': true,
        EXCHANGE_INLINE_ENABLED: true,
      } as any,
      policyTypes: data.policyTypes[guid],
      pocProvisionServices: [false, false, false, false],
    };
    for (const item of Object.values(dict)) {
      const cfg = CONFIG[item.type];
      if (cfg.computeHidden) {
        args.self = item;
        item.hidden = cfg.computeHidden(args) ?? item.hidden;
      }
      if (args.policyTypes.length <= 0 && args.config['STORAGE_ENABLED']) {
        if (item.type === 'NO_SERVICE') {
          item.hidden = false;
        } else {
          item.hidden = true;
        }
      }
    }
  }

  return allNotifications;
}

function generateDefault() {
  const dict: NotificationDict = {};
  for (const [typ, cfg] of Object.entries(CONFIG)) {
    dict[typ] = {
      type: typ,
      hidden: cfg.defaultHidden,
      status: cfg.defaultStatus,
      time: null,
    };
  }
  return dict;
}
